// useCategoryItemScreen.ts (веб) - с исправленной пагинацией
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCallback, useState, useEffect, useMemo } from "react";
import { selectCategoryList } from "../../store/slices/categoryListSlice";
import {
  selectHasUseStock,
  upsertItems,
} from "../../store/slices/saleItemListSlice";
import { selectCurrentUser } from "../../store/slices/currentUserSlice";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../store/slices/storeListSlice";
import { GroupedProduct, SaleItemInterface } from "../../common/types/types";
import { ROUTES } from "../../common/utils/constants";
import { getApi } from "../../api/api";
import { useScrollPagination } from "../../common/hooks/useScrollPagination";
import { groupProducts } from "../../common/utils/functions/groupProducts";
import { checkItemAvailability } from "../../common/utils/functions/checkItemAvailability";

export const useCategoryItemScreen = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const categoryId = Number(id);

  const [rawItems, setRawItems] = useState<SaleItemInterface[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const categoryList = useSelector(selectCategoryList) || [];
  const currentUser = useSelector(selectCurrentUser);
  const usingStock = useSelector(selectHasUseStock);
  const currentCluster = useSelector(selectCurrentCluster);
  const forceStore = currentCluster?.forceStore ?? false;
  const selectedStoreIds = useSelector(selectForceStores);

  const category = categoryList.find((cat) => String(cat.id) === String(id));

  // Группировка с поиском и фильтром по остаткам
  const items = useMemo(() => {
    let result = rawItems;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q) ||
          item.id?.toString().includes(q) ||
          item.barCode?.toLowerCase().includes(q),
      );
    }

    // ❌ Фильтр по остаткам (если showOutOfStock = false)
    if (!currentCluster?.showOutOfStock) {
      result = result.filter(
        (item) =>
          checkItemAvailability({
            item,
            forceStore,
            selectedStoreIds,
          }).available,
      );
    }

    return groupProducts(result);
  }, [
    rawItems,
    searchQuery,
    usingStock,
    currentCluster?.showOutOfStock,
    forceStore,
    selectedStoreIds,
  ]);

  const loadFirstPage = useCallback(async () => {
    if (!categoryId) return;
    setIsFirstLoading(true);
    try {
      const api = getApi();
      const response = await api.getSaleItemList({
        limit: 50,
        offset: 0,
        ...(categoryId && { categoryId }),
      });
      if (response?.data) {
        setRawItems(response.data);
        if (response.data.length > 0) {
          dispatch(upsertItems(response.data));
        }
        setHasMore(
          response.data.length === 50 && response.data.length < response.total,
        );
        setTotalItems(response.total);
      }
    } catch (error) {
      console.error("Failed to load first page:", error);
    } finally {
      setIsFirstLoading(false);
    }
  }, [categoryId, dispatch]);

  useEffect(() => {
    if (categoryId) {
      loadFirstPage();
    }
  }, [categoryId, loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || searchQuery) return;

    setLoadingMore(true);
    try {
      const api = getApi();
      const offset = rawItems.length;
      const response = await api.getSaleItemList({
        limit: 50,
        offset,
        ...(categoryId && { categoryId }),
      });

      if (response?.data?.length) {
        const newRawItems = [...rawItems, ...response.data];
        setRawItems(newRawItems);
        dispatch(upsertItems(response.data));
        setHasMore(response.data.length === 50);
        setTotalItems(response.total);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Load more error:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [rawItems, loadingMore, hasMore, searchQuery, categoryId, dispatch]);

  const { lastItemRef } = useScrollPagination(hasMore, loadingMore, loadMore);

  const getCategoryName = (categoryId: number) => {
    return categoryList.find((cat) => cat.id === categoryId)?.name || "";
  };

  const handleGroupPress = (group: GroupedProduct) => {
    navigate(`${ROUTES.GROUP}/${group.groupId}`);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleSearchClear = () => {
    setSearchQuery("");
  };

  return {
    category,
    items,
    getCategoryName,
    isAuth: Boolean(currentUser?.id),
    usingStock,
    handleGroupPress,
    searchQuery,
    searchLoading: false,
    loadingMore,
    hasMore,
    loadMore,
    lastItemRef,
    handleSearchChange,
    handleSearchClear,
    totalItems,
    isFirstLoading,
  };
};
